/**
 * Camlin Speech ASR engine for the SDK mic path.
 *
 * Same contract as the Azure `sttFromMic` (audioUtils.ts) — same
 * callbacks, same "press, speak, release-on-silence" turn shape — but
 * recognition goes through the Camlin Speech STT API:
 *
 *   1. GET  <tokenUrl>                      -> {token: "cat_…", expires_at}
 *      (a server route holding the real key; see /api/v1/asr/token)
 *   2. capture mic PCM, endpoint on silence  (RMS gate, below)
 *   3. POST <recognizeUrl>  body=WAV  Authorization: Bearer cat_…
 *      -> {text, confidence, words…} — with the minting tenant's
 *      lexicon biasing applied server-side.
 *
 * Batch, not streaming, on purpose: the STT API's streaming surface is
 * Socket.IO with UI-server-minted demo-tier session tokens and no
 * lexicon support in the streaming pipeline yet (audio_handler.py), so
 * the honest production path today is recognize. The turn UX is
 * unchanged from the Azure recognizeOnce shape; there are no interim
 * transcripts. See the api-productionisation plan for the streaming
 * follow-up.
 *
 * Capture uses a ScriptProcessorNode. Deprecated, but universally
 * supported and already the pattern this SDK's WebRTC path relies on;
 * an AudioWorklet needs a separately-served module file, which a
 * single-file IIFE embed cannot assume it can host.
 */

import { getSttEngineConfig, camlinSpeechPhraseHeaders } from './sttEngine';

// ---------------------------------------------------------------------------
// Token cache — refresh ahead of expiry so a turn never races the TTL.
// ---------------------------------------------------------------------------

interface CachedToken {
  token: string;
  expiresAt: number; // epoch seconds
}

let cachedToken: CachedToken | null = null;

const TOKEN_REFRESH_MARGIN_S = 30;

async function getAsrToken(tokenUrl: string): Promise<string> {
  const now = Date.now() / 1000;
  if (cachedToken && cachedToken.expiresAt - TOKEN_REFRESH_MARGIN_S > now) {
    return cachedToken.token;
  }
  const res = await fetch(tokenUrl, { cache: 'no-store', headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`ASR token endpoint returned ${res.status}`);
  }
  const data = (await res.json()) as { token?: unknown; expires_at?: unknown };
  if (typeof data.token !== 'string' || !data.token.trim()) {
    throw new Error('ASR token endpoint returned no token');
  }
  cachedToken = {
    token: data.token.trim(),
    expiresAt: typeof data.expires_at === 'number' ? data.expires_at : now + 240
  };
  return cachedToken.token;
}

/** Test hook / hard reset. */
export function clearAsrTokenCache(): void {
  cachedToken = null;
}

/**
 * Mint (or reuse a cached) Camlin Speech token. Used by the STT
 * dispatcher to fail-fast onto Azure when `/api/asr-token` is 503
 * (no `CAMLIN_SPEECH_API_KEY`) instead of capturing audio that can
 * never be transcribed.
 */
export async function probeCamlinAsrToken(tokenUrl: string): Promise<string> {
  return getAsrToken(tokenUrl);
}

// ---------------------------------------------------------------------------
// Armed capture resources (round 5, Keith's real-device ASR report)
//
// The 2.4.0 turn shape tore everything down after each utterance: stream
// tracks stopped, AudioContext closed, and the next tap paid getUserMedia +
// context construction + a token round-trip BEFORE capture began — the
// "misses the first word or two" and "takes a while to start listening"
// defects. Now the stream and context stay armed between turns for a short
// window (the browser's mic-in-use indicator stays on while armed, which is
// truthful — we hold the device), and the token fetch runs in parallel with
// capture because it is only needed at recognize-POST time, seconds later.
// ---------------------------------------------------------------------------

const ARMED_STREAM_RELEASE_MS = 60000;

let armedAudioContext: AudioContext | null = null;
let armedStreamReleaseTimer: ReturnType<typeof setTimeout> | null = null;

export function getSharedStream(): MediaStream | null {
  const shared = (window as unknown as { camlinSharedAudioStream?: MediaStream | null })
    .camlinSharedAudioStream;
  const live = shared
    && shared.active
    && shared.getAudioTracks().some((t) => t.readyState === 'live' && !t.muted);
  return live ? shared! : null;
}

export function setSharedStream(stream: MediaStream | null): void {
  (window as unknown as { camlinSharedAudioStream?: MediaStream | null }).camlinSharedAudioStream = stream;
}

export function cancelArmedStreamRelease(): void {
  if (armedStreamReleaseTimer) {
    clearTimeout(armedStreamReleaseTimer);
    armedStreamReleaseTimer = null;
  }
}

/**
 * Keep the mic armed for a follow-up turn, releasing it after a quiet
 * minute so the browser's recording indicator doesn't outstay the visit.
 */
export function scheduleArmedStreamRelease(): void {
  cancelArmedStreamRelease();
  armedStreamReleaseTimer = setTimeout(() => {
    armedStreamReleaseTimer = null;
    const shared = getSharedStream();
    if (shared) {
      shared.getTracks().forEach((track) => track.stop());
      setSharedStream(null);
    }
    if (armedAudioContext) {
      void armedAudioContext.close().catch(() => { /* already closed */ });
      armedAudioContext = null;
    }
    console.log('[camlinAsr] armed mic released after idle window');
  }, ARMED_STREAM_RELEASE_MS);
}

export async function getArmedAudioContext(): Promise<AudioContext> {
  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  if (!armedAudioContext || armedAudioContext.state === 'closed') {
    armedAudioContext = new AudioContextCtor();
  }
  if (armedAudioContext.state === 'suspended') {
    await armedAudioContext.resume();
  }
  return armedAudioContext;
}

/** The capture-truth event the Companion's voice strip tracks (round 5
 * item 2): phases 'capturing' | 'processing' | 'ended', dispatched from
 * the exact points where capture actually starts, endpointing completes,
 * and the turn finishes. */
export function dispatchCapturePhase(phase: 'capturing' | 'processing' | 'ended'): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('avatar:mic-capture', { detail: { phase } }));
}

// ---------------------------------------------------------------------------
// PCM capture with RMS endpointing
// ---------------------------------------------------------------------------

const TARGET_SAMPLE_RATE = 16000;

// Endpointing knobs. Tuned to match the Azure path's feel: ~3s of leading
// silence gives up, ~1.4s of trailing silence ends the utterance.
const SPEECH_RMS_THRESHOLD = 0.012;
const LEADING_SILENCE_MS = 3500;
const TRAILING_SILENCE_MS = 1400;
const MAX_UTTERANCE_MS = 15000;

interface CaptureResult {
  wav: ArrayBuffer;
  durationMs: number;
  heardSpeech: boolean;
}

function captureUtterance(stream: MediaStream, audioContext: AudioContext): Promise<CaptureResult> {
  return new Promise((resolve, reject) => {
    try {
      const source = audioContext.createMediaStreamSource(stream);
      // 4096 frames ≈ 85ms at 48kHz — fine-grained enough for endpointing.
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      const chunks: Float32Array[] = [];
      const inputRate = audioContext.sampleRate;
      const startedAt = performance.now();
      let lastSpeechAt: number | null = null;
      let finished = false;

      const finish = () => {
        if (finished) return;
        finished = true;
        try {
          processor.disconnect();
          source.disconnect();
        } catch {
          /* already torn down */
        }
        const durationMs = performance.now() - startedAt;
        const pcm = mergeChunks(chunks);
        const downsampled = downsample(pcm, inputRate, TARGET_SAMPLE_RATE);
        resolve({
          wav: encodeWavPcm16(downsampled, TARGET_SAMPLE_RATE),
          durationMs,
          heardSpeech: lastSpeechAt !== null
        });
      };

      processor.onaudioprocess = (event) => {
        if (finished) return;
        const data = event.inputBuffer.getChannelData(0);
        chunks.push(new Float32Array(data));

        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
        const rms = Math.sqrt(sum / data.length);
        const now = performance.now();

        if (rms >= SPEECH_RMS_THRESHOLD) {
          lastSpeechAt = now;
        }

        const elapsed = now - startedAt;
        if (elapsed >= MAX_UTTERANCE_MS) return finish();
        if (lastSpeechAt === null) {
          if (elapsed >= LEADING_SILENCE_MS) return finish();
        } else if (now - lastSpeechAt >= TRAILING_SILENCE_MS) {
          return finish();
        }
      };

      source.connect(processor);
      // ScriptProcessor only fires when connected to a destination; route
      // it through a zero-gain node so capture is silent.
      const sink = audioContext.createGain();
      sink.gain.value = 0;
      processor.connect(sink);
      sink.connect(audioContext.destination);
    } catch (error) {
      reject(error);
    }
  });
}

function mergeChunks(chunks: Float32Array[]): Float32Array {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const merged = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  return merged;
}

export function downsample(input: Float32Array, inputRate: number, targetRate: number): Float32Array {
  if (inputRate === targetRate) return input;
  const ratio = inputRate / targetRate;
  const length = Math.floor(input.length / ratio);
  const output = new Float32Array(length);
  // Average over each source window: cheap anti-aliasing that is plenty
  // for speech into a 16k model.
  for (let i = 0; i < length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    output[i] = end > start ? sum / (end - start) : 0;
  }
  return output;
}

function encodeWavPcm16(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeString = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

// ---------------------------------------------------------------------------
// The engine entry point — same signature as the Azure sttFromMic
// ---------------------------------------------------------------------------

export async function sttFromMicCamlin(
  setDisplayText: (text: string) => void,
  setUserTalking: (value: boolean) => void,
  setRecord: (value: boolean) => void,
  setSpeak: (value: boolean) => void,
  askCamlin: (text: string) => void,
  sendMsgToBot: (msg: string, direction: string) => void,
  _onInterimTranscript?: (text: string) => void
): Promise<void> {
  const engine = getSttEngineConfig();
  const tokenUrl = engine.tokenUrl;
  const recognizeUrl = engine.recognizeUrl || '/api/v1/asr/recognize';
  if (!tokenUrl) {
    throw new Error('Camlin ASR engine is not configured (missing tokenUrl)');
  }

  const tapAt = performance.now();
  cancelArmedStreamRelease();

  // Round-5 turn shape (Keith's real-device report, 6 Aug 2026):
  //   tap ──> stream (usually already armed) ──> capture STARTS ──┐
  //        └> token fetch (parallel, cached for its TTL) ─────────┴> POST
  // Nothing network-shaped sits between the tap and the first buffered
  // sample any more, and the stream/context stay armed after the turn so
  // a follow-up tap starts capture in single-digit milliseconds.
  const cleanup = async (outcome: 'ok' | 'error') => {
    setUserTalking(false);
    setSpeak(false);
    setRecord(false);
    dispatchCapturePhase('ended');
    window.currentAudioStream = null;
    if (outcome === 'error') {
      // A failed turn releases the device immediately (the next tap
      // re-acquires); a clean turn keeps it armed for the follow-up.
      cancelArmedStreamRelease();
      const shared = getSharedStream();
      if (shared) {
        shared.getTracks().forEach((track) => track.stop());
        setSharedStream(null);
      }
      if (armedAudioContext) {
        void armedAudioContext.close().catch(() => { /* already closed */ });
        armedAudioContext = null;
      }
    } else {
      scheduleArmedStreamRelease();
    }
  };

  try {
    setDisplayText('Initializing audio...');

    // Token fetch starts NOW but is not awaited until the recognize POST —
    // it must never delay capture start (it ate the first words in 2.4.0).
    const tokenPromise = getAsrToken(tokenUrl);
    tokenPromise.catch(() => { /* surfaced at await below; avoid unhandled rejection */ });

    // Shared-stream reuse (armed from the previous turn, or cached by the
    // permission grant in useVoiceBrowserAccess); fresh acquisition only
    // when neither exists.
    let stream = getSharedStream();
    if (!stream) {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1
        }
      });
      setSharedStream(stream);
    }
    window.currentAudioStream = stream;

    const audioContext = await getArmedAudioContext();

    setDisplayText('Speak now!');
    setUserTalking(true);
    setRecord(true);

    const capturePromise = captureUtterance(stream, audioContext);
    dispatchCapturePhase('capturing');
    console.log(`[camlinAsr] capture armed in ${Math.round(performance.now() - tapAt)}ms`);

    const capture = await capturePromise;

    setRecord(false);
    setUserTalking(false);
    dispatchCapturePhase('processing');

    if (!capture.heardSpeech) {
      setDisplayText('No speech detected. Please try again.');
      await cleanup('ok');
      return;
    }

    setDisplayText('Recognizing...');
    const token = await tokenPromise;
    const headers: Record<string, string> = {
      'Content-Type': 'audio/wav',
      Authorization: `Bearer ${token}`
    };
    if (engine.language) {
      headers['x-camlin-asr-language'] = engine.language;
    }
    const phraseHeaders = camlinSpeechPhraseHeaders();
    Object.assign(headers, phraseHeaders);
    const response = await fetch(recognizeUrl, {
      method: 'POST',
      headers,
      body: capture.wav
    });
    if (response.status === 401) {
      // Token likely expired mid-turn; drop the cache so the next press
      // mints fresh rather than failing the same way.
      clearAsrTokenCache();
      throw new Error('Speech recognition session expired. Please try again.');
    }
    if (!response.ok) {
      throw new Error(`Speech recognition failed (${response.status})`);
    }
    const result = (await response.json()) as { text?: unknown };
    const text = typeof result.text === 'string' ? result.text.trim().replace(/[.!?]+$/, '') : '';
    if (text) {
      setDisplayText(`Processing: ${text}`);
      await askCamlin(text);
      sendMsgToBot(text, 'out');
      setDisplayText(`You said: ${text}`);
    } else {
      setDisplayText('No speech detected. Please try again.');
    }
    await cleanup('ok');
  } catch (error) {
    await cleanup('error');
    console.error('[camlinAsr] sttFromMicCamlin error:', error);
    throw error;
  }
}
